import { cn } from '@utils/cn';
import React, { useCallback } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { InvoiceLineItem, InvoiceLineItemSourceType } from '@models/invoice.model';

interface InvoiceLineItemRowProps {
  item: InvoiceLineItem;
  editable: boolean;
  currency?: string;
  onChange?: (next: InvoiceLineItem) => void;
  onRemove?: () => void;
  className?: string;
}

const SOURCE_LABEL: Record<InvoiceLineItemSourceType, string> = {
  [InvoiceLineItemSourceType.TASK_MATERIAL]: 'Material',
  [InvoiceLineItemSourceType.TASK_EQUIPMENT]: 'Equipment',
  [InvoiceLineItemSourceType.LINE_ITEM]: 'Line Item',
  [InvoiceLineItemSourceType.DELEGATED_TASK_COST]: 'Delegated',
};

const formatCurrency = (amount: number, currency = 'USD'): string => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

export const InvoiceLineItemRow: React.FC<InvoiceLineItemRowProps> = ({
  item,
  editable,
  currency = 'USD',
  onChange,
  onRemove,
  className = '',
}) => {
  const isLinked = Boolean(item.sourceType && item.sourceEntityId);
  const sourceLabel = item.sourceType ? SOURCE_LABEL[item.sourceType] : null;

  const update = useCallback(
    (patch: Partial<InvoiceLineItem>) => {
      if (!onChange) return;
      const next = { ...item, ...patch };
      const quantity = Number(next.quantity ?? 0);
      const unitPrice = Number(next.unitPrice ?? 0);
      const discount = Number(next.discountAmount ?? 0);
      next.lineTotal = Number((quantity * unitPrice - discount).toFixed(2));
      onChange(next);
    },
    [item, onChange],
  );

  return (
    <tr className={cn('border-b border-gray-100', className)}>
      <td className="px-3 py-2 align-top">
        <div className="flex flex-col gap-1">
          {editable && !isLinked ? (
            <input
              type="text"
              value={item.description}
              onChange={(e) => update({ description: e.target.value })}
              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
              placeholder="Description"
            />
          ) : (
            <span className="text-sm text-gray-900">{item.description}</span>
          )}
          {sourceLabel && (
            <span className="text-xs text-indigo-600 uppercase tracking-wide">
              {sourceLabel}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-top w-28">
        {editable && !isLinked ? (
          <input
            type="number"
            min={0}
            step="0.0001"
            value={item.quantity}
            onChange={(e) => update({ quantity: Number(e.target.value) })}
            className="w-full px-2 py-1 text-sm text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
          />
        ) : (
          <span className="text-sm text-gray-700 block text-right">{item.quantity}</span>
        )}
      </td>
      <td className="px-3 py-2 align-top w-32">
        {editable && !isLinked ? (
          <input
            type="number"
            min={0}
            step="0.01"
            value={item.unitPrice}
            onChange={(e) => update({ unitPrice: Number(e.target.value) })}
            className="w-full px-2 py-1 text-sm text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
          />
        ) : (
          <span className="text-sm text-gray-700 block text-right">
            {formatCurrency(item.unitPrice, currency)}
          </span>
        )}
      </td>
      <td className="px-3 py-2 align-top w-32 text-right">
        <span className="text-sm font-medium text-gray-900">
          {formatCurrency(item.lineTotal, currency)}
        </span>
      </td>
      {editable && onRemove && (
        <td className="px-3 py-2 align-top w-10">
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-gray-400 hover:text-red-500 rounded"
            aria-label="Remove line item">
            <DeleteOutlineIcon className="w-4 h-4" />
          </button>
        </td>
      )}
    </tr>
  );
};
